import React, { useContext, useState } from "react";
import { User, AtSign, Mail, ShieldCheck, CalendarDays, Clock3, Copy, Check, Pencil, ChevronRight, KeyRound, Monitor, LogOut, Headphones, Grid2X2, CheckCircle2 } from "lucide-react";
import { FaGoogle, FaWhatsapp } from "react-icons/fa";
import { AuthContext } from "src/context/AuthContext";

function Profile() {
  const [copied, setCopied] = useState(false);
  const { logout } = useContext(AuthContext);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");

  const userName = user?.data?.name || "User";
  const username = user?.data?.username || "username";
  const email = user?.data?.email || "-";
  const userId = user?.data?.id || "-";
  const googleId = user?.data?.google_id || null;
  const emailVerifiedAt = user?.data?.email_verified_at || null;
  const createdAt = user?.data?.created_at || null;
  const updatedAt = user?.data?.updated_at || null;

  const getInitials = (name = "") => {
    if (!name.trim()) return "U";
    return name.trim().split(/\s+/).map((word) => word.charAt(0)).join("").slice(0, 2).toUpperCase();
  };

  const formatDate = (date) => {
    if (!date || Number.isNaN(new Date(date).getTime())) return "-";
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatDateTime = (date) => {
    if (!date || Number.isNaN(new Date(date).getTime())) return "-";
    return new Date(date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
  };

  const copyUserId = async () => {
    if (!user?.id) return;
    try {
      await navigator.clipboard.writeText(user.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Unable to copy user ID:", error);
    }
  };

  const handleEditProfile = () => console.log("Edit profile clicked");
  const handleChangePassword = () => console.log("Change password clicked");
  const handleManageSessions = () => console.log("Manage sessions clicked");
  const handleLogout = async () => logout();
  const handleSupport = () => console.log("Contact support clicked");

  return (
    <div className="relative min-h-full overflow-hidden bg-app-bg bg-cover bg-center bg-fixed" style={{ backgroundImage: "url('/images/profile-bg.png')" }}>
      <div className="pointer-events-none absolute inset-0 bg-slate-50/75 dark:bg-dark-bg/90" />

      <div className="relative z-10 mx-auto max-w-[1280px] px-4 py-3">
        {/* Profile Hero */}
        <section className="relative mb-3 overflow-hidden rounded-xl border border-border-light bg-card-bg/95 shadow-soft backdrop-blur-sm dark:border-dark-border dark:bg-dark-card/95">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-theme-50/80 dark:from-dark-card/95 dark:via-dark-card/90 dark:to-dark-soft-blue/70" />
          <div className="pointer-events-none absolute right-0 top-0 h-28 w-44 opacity-30" style={{ backgroundImage: "radial-gradient(#2493d3 1px, transparent 1px)", backgroundSize: "10px 10px", maskImage: "linear-gradient(to left, black, transparent)", WebkitMaskImage: "linear-gradient(to left, black, transparent)" }} />

          <div className="relative flex min-h-[125px] items-center px-5 py-3">
            <div className="relative shrink-0">
              <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border-[3px] border-white bg-gradient-to-br from-secondary to-primary text-3xl font-bold text-white shadow-blue dark:border-dark-card">
                {getInitials(userName)}
              </div>

              <button type="button" onClick={handleEditProfile} title="Edit profile" className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-white text-text-secondary shadow-soft transition hover:text-primary dark:border-dark-card dark:bg-dark-card dark:text-dark-text-secondary dark:hover:text-dark-primary">
                <Pencil size={10} />
              </button>
            </div>

            <div className="ml-4 min-w-0">
              <h1 className="text-[21px] font-bold leading-tight text-text-primary dark:text-dark-text-primary">{userName}</h1>
              <p className="mt-0.5 text-xs font-medium text-primary dark:text-dark-primary">@{username}</p>

              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {emailVerifiedAt && (
                  <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-success dark:bg-emerald-500/10 dark:text-emerald-400">
                    <CheckCircle2 size={10} /> Email Verified
                  </span>
                )}

                <span className="flex items-center gap-1 text-[9px] text-text-secondary dark:text-dark-text-secondary">
                  <CalendarDays size={11} /> Member since {formatDate(createdAt)}
                </span>
              </div>
            </div>

            <div className="absolute right-8 hidden md:block">
              <div className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-primary text-white shadow-blue">
                <FaWhatsapp size={30} />
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
          {/* Profile Information */}
          <section className="overflow-hidden rounded-xl border border-border-light bg-card-bg/95 shadow-soft backdrop-blur-sm dark:border-dark-border dark:bg-dark-card/95">
            <div className="flex items-center gap-2.5 px-4 py-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                <User size={14} />
              </div>

              <div>
                <h2 className="text-[13px] font-bold text-text-primary dark:text-dark-text-primary">Profile Information</h2>
                <p className="text-[9px] text-text-secondary dark:text-dark-text-secondary">Your account information</p>
              </div>
            </div>

            <div className="mx-4 h-px bg-border-light dark:bg-dark-border-light" />

            <div className="px-4">
              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><User size={13} /> Full Name</div>
                <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{userName}</span>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><AtSign size={13} /> Username</div>
                <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{username}</span>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><Mail size={13} /> Email Address</div>
                <span className="truncate text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{email}</span>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><ShieldCheck size={13} /> Email Verified At</div>
                <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{formatDateTime(emailVerifiedAt)}</span>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><FaGoogle size={12} /> Google ID</div>
                {googleId ? (
                  <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{googleId}</span>
                ) : (
                  <span className="w-fit rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600 dark:bg-dark-surface dark:text-dark-text-secondary">Not connected</span>
                )}
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><Grid2X2 size={12} /> User ID</div>
                <div className="flex min-w-0 items-center gap-1">
                  <span className="truncate font-mono text-[9px] text-text-primary dark:text-dark-text-primary" title={userId}>{userId}</span>
                  {user?.id && (
                    <button type="button" onClick={copyUserId} title="Copy user ID" className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-primary transition hover:bg-theme-50 dark:text-dark-primary dark:hover:bg-dark-soft-blue">
                      {copied ? <Check size={11} /> : <Copy size={11} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center border-b border-border-light dark:border-dark-border-light sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><CalendarDays size={13} /> Created At</div>
                <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{formatDateTime(createdAt)}</span>
              </div>

              <div className="grid min-h-[40px] grid-cols-[150px_minmax(0,1fr)] items-center sm:grid-cols-[170px_minmax(0,1fr)]">
                <div className="flex items-center gap-2 text-[10px] text-text-secondary dark:text-dark-text-secondary"><Clock3 size={13} /> Updated At</div>
                <span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">{formatDateTime(updatedAt)}</span>
              </div>
            </div>
          </section>

          {/* Right Column */}
          <div className="space-y-3">
            {/* Account Status */}
            <section className="overflow-hidden rounded-xl border border-border-light bg-card-bg/95 shadow-soft backdrop-blur-sm dark:border-dark-border dark:bg-dark-card/95">
              <div className="flex items-center gap-2.5 px-4 py-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                  <ShieldCheck size={14} />
                </div>
                <h2 className="text-[13px] font-bold text-text-primary dark:text-dark-text-primary">Account Status</h2>
              </div>

              <div className="px-4 pb-3">
                <div className="flex items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50/70 p-2.5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success text-white">
                    <Check size={14} strokeWidth={3} />
                  </div>

                  <div>
                    <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400">Active Account</p>
                    <p className="mt-0.5 text-[8px] text-text-secondary dark:text-dark-text-secondary">Your account is active and in good standing.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Actions */}
            <section className="overflow-hidden rounded-xl border border-border-light bg-card-bg/95 shadow-soft backdrop-blur-sm dark:border-dark-border dark:bg-dark-card/95">
              <div className="flex items-center gap-2.5 px-4 py-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                  <KeyRound size={14} />
                </div>
                <h2 className="text-[13px] font-bold text-text-primary dark:text-dark-text-primary">Quick Actions</h2>
              </div>

              <div className="mx-4 mb-3 overflow-hidden rounded-lg border border-border-light dark:border-dark-border">
                <button type="button" onClick={handleEditProfile} className="group flex h-9 w-full items-center justify-between border-b border-border-light px-2.5 transition-colors hover:bg-theme-50 dark:border-dark-border-light dark:hover:bg-dark-surface">
                  <span className="flex items-center gap-2"><Pencil size={12} className="text-text-secondary dark:text-dark-text-secondary" /><span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">Edit Profile</span></span>
                  <ChevronRight size={12} className="text-text-muted" />
                </button>

                <button type="button" onClick={handleChangePassword} className="group flex h-9 w-full items-center justify-between border-b border-border-light px-2.5 transition-colors hover:bg-theme-50 dark:border-dark-border-light dark:hover:bg-dark-surface">
                  <span className="flex items-center gap-2"><KeyRound size={12} className="text-text-secondary dark:text-dark-text-secondary" /><span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">Change Password</span></span>
                  <ChevronRight size={12} className="text-text-muted" />
                </button>

                <button type="button" onClick={handleManageSessions} className="group flex h-9 w-full items-center justify-between border-b border-border-light px-2.5 transition-colors hover:bg-theme-50 dark:border-dark-border-light dark:hover:bg-dark-surface">
                  <span className="flex items-center gap-2"><Monitor size={12} className="text-text-secondary dark:text-dark-text-secondary" /><span className="text-[10px] font-medium text-text-primary dark:text-dark-text-primary">Manage Sessions</span></span>
                  <ChevronRight size={12} className="text-text-muted" />
                </button>

                <button type="button" onClick={handleLogout} className="group flex h-9 w-full items-center justify-between px-2.5 transition-colors hover:bg-red-50 dark:hover:bg-red-500/5">
                  <span className="flex items-center gap-2"><LogOut size={12} className="text-danger" /><span className="text-[10px] font-medium text-danger">Log Out</span></span>
                  <ChevronRight size={12} className="text-red-400" />
                </button>
              </div>
            </section>

            {/* Help */}
            <section className="rounded-xl border border-border-light bg-card-bg/95 p-3 shadow-soft backdrop-blur-sm dark:border-dark-border dark:bg-dark-card/95">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
                  <Headphones size={13} />
                </div>

                <div>
                  <h3 className="text-[10px] font-semibold text-text-primary dark:text-dark-text-primary">Need Help?</h3>
                  <p className="text-[8px] text-text-secondary dark:text-dark-text-secondary">Contact support for assistance.</p>
                </div>
              </div>

              <button type="button" onClick={handleSupport} className="mt-2 flex h-7 w-full items-center justify-between rounded-md border border-theme-200 bg-theme-50 px-2.5 text-[9px] font-semibold text-primary transition hover:bg-theme-100 dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary dark:hover:bg-dark-blue-bg">
                Contact Support
                <ChevronRight size={11} />
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
